import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { useScanSession } from '@/features/scanner/ScanSessionContext';
import { prepareBillImage, ImageDecodeError } from '@/services/image/prepareImage';

export default function HomePage() {
  const navigate = useNavigate();
  const { setPrepared } = useScanSession();
  const uploadInputRef = useRef<HTMLInputElement>(null);

  async function handleFileChosen(file: File) {
    try {
      const prepared = await prepareBillImage(file);
      setPrepared(prepared);
      navigate('/scan/preview');
    } catch (err) {
      const message = err instanceof ImageDecodeError ? err.message : 'Something went wrong reading that photo.';
      // Preview page is the natural place to show a retake/error state; for
      // an upload-picker failure before we even have a session, a simple
      // alert keeps this from being a dead end. Replaced by inline UI once
      // an error-toast system exists (see docs/architecture.md follow-ups).
      window.alert(message);
    }
  }

  return (
    <div className="flex flex-col gap-6 p-4 pt-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-900">Bill Scanner</h1>
        <p className="mt-1 text-sm text-slate-500">Scan a bill and get it ready for the IRD prize draw.</p>
      </header>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => navigate('/scan')}
          className="flex items-center justify-center gap-3 rounded-2xl bg-blue-700 px-6 py-5 text-lg font-semibold text-white shadow-sm active:bg-blue-800"
        >
          <span aria-hidden="true" className="text-2xl">
            📷
          </span>
          Scan New Bill
        </button>

        <button
          type="button"
          onClick={() => uploadInputRef.current?.click()}
          className="rounded-2xl border border-slate-300 bg-white px-6 py-4 text-base font-medium text-slate-700 active:bg-slate-100"
        >
          Upload Bill
        </button>
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void handleFileChosen(file);
          }}
        />
      </div>

      <section aria-labelledby="recent-bills-heading" className="flex flex-col gap-3">
        <h2 id="recent-bills-heading" className="text-base font-semibold text-slate-900">
          Recent Bills
        </h2>
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Your scanned bills will appear here.
        </div>
      </section>
    </div>
  );
}
